
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AIAnalysisResult, NotificationProductData } from "@/types/notifications";
import { useNotificationUpdate } from "./useNotificationUpdate";

export interface Notification {
  id: string;
  type: string;
  vo_number: string | null;
  exact_order_number: string | null;
  message: string;
  priority: string;
  status: string;
  user_id: string;
  user_name: string;
  created_at: string;
  updated_at: string;
  archived: boolean;
  is_deleted: boolean;
  deleted_at: string | null;
  products_monitored?: boolean;
  products?: NotificationProductData[];
}

export interface NotificationEditHistory {
  id: string;
  notification_id: string;
  editor_id: string;
  editor_name: string;
  edit_date: string;
  previous_message: string | null;
  new_message: string | null;
  field_name: string | null;
  previous_value: string | null;
  new_value: string | null;
  change_type: string | null;
}

export const useNotificationDetail = (id: string | undefined) => {
  const [editMode, setEditMode] = useState(false);
  const [editedNotification, setEditedNotification] = useState<Notification | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showEditHistory, setShowEditHistory] = useState(false);

  // Fetch notification details
  const {
    data: notification,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["notification", id],
    queryFn: async () => {
      if (!id) throw new Error("Notification ID is required");

      console.log("Fetching notification details for ID:", id);

      try {
        // Get notification details
        const { data: notificationData, error: notificationError } = await supabase
          .from("notifications")
          .select("*")
          .eq("id", id)
          .maybeSingle();

        if (notificationError) {
          console.error("Error fetching notification:", notificationError);
          throw new Error(`Failed to fetch notification: ${notificationError.message}`);
        }

        if (!notificationData) {
          throw new Error("Notification not found");
        }

        console.log("Fetched notification data:", notificationData);

        // Get related products
        const { data: productsData, error: productsError } = await supabase
          .from("notification_product_monitoring")
          .select("*")
          .eq("notification_id", id);

        if (productsError) {
          console.error("Error fetching products:", productsError);
          // Don't throw here, we can still continue with the notification data
        } else {
          console.log("Fetched products data:", productsData || []);
        }

        // Return notification with products if available
        return {
          ...notificationData,
          products: productsData || []
        };
      } catch (error) {
        console.error("Error in notification query function:", error);
        throw error;
      }
    },
    enabled: !!id,
    retry: (failureCount, error) => {
      // Don't retry if it's a "not found" error
      if (error instanceof Error && error.message.includes("not found")) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    }
  });

  // Fetch edit history
  const {
    data: editHistory,
    isLoading: isLoadingHistory
  } = useQuery({
    queryKey: ["notification-history", id],
    queryFn: async () => {
      if (!id) throw new Error("Notification ID is required");

      console.log("Fetching notification history for ID:", id);

      const { data, error: historyError } = await supabase
        .from("notification_edit_history")
        .select("*")
        .eq("notification_id", id)
        .order("edit_date", { ascending: false });

      if (historyError) {
        console.error("Error fetching notification history:", historyError);
        throw historyError;
      }

      console.log("Fetched notification history:", data || []);
      return data;
    },
    enabled: !!id && showEditHistory
  });

  // Set edited notification when notification data changes
  useEffect(() => {
    if (notification) {
      console.log("Setting edited notification from fetched data:", notification);
      setEditedNotification(notification);
    }
  }, [notification]);

  const {
    updateNotification,
    handleInputChange,
    handleSubmit,
    resetNotification,
    handleAIAnalysisComplete,
    handleProductsChange
  } = useNotificationUpdate(
    id,
    notification,
    editedNotification,
    setEditedNotification,
    setEditMode
  );

  return {
    notification,
    editedNotification,
    editMode,
    setEditMode,
    isAnalyzing,
    setIsAnalyzing,
    showEditHistory,
    setShowEditHistory,
    isLoading,
    error,
    editHistory,
    isLoadingHistory,
    handleInputChange,
    handleAIAnalysisComplete,
    handleProductsChange,
    handleSubmit,
    resetNotification,
    updateNotification,
    refetch
  };
};
